import { useEffect, useState } from 'react';
import { getMyAgencyDocumentDownload, getMyAgencyProfile } from '@/lib/api';
import { fullRegionName } from '@/lib/addressRegions';
import AccountSettingsCard from '@/components/profile/AccountSettingsCard';
import ProfileHero from '@/components/profile/ProfileHero';
import AgencyAgreementCard from '@/components/agency/AgencyAgreementCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Building2,
  Download,
  FileCheck2,
  Globe2,
  Mail,
  MapPin,
  Phone,
  Target,
  UserRound,
  Users,
} from 'lucide-react';
import {
  EMPTY_VALUE,
  InfoRow,
  StatusBadge,
  contactName,
  displayAgencyName,
  formatDate,
  safeList,
} from './AgencyPortalShared';

function initials(name) {
  return String(name || 'Agency')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
}

function BadgeList({ values, numbered = false }) {
  const items = safeList(values).filter(Boolean);
  if (!items.length) return <span className="text-[#062B1A]/40">{EMPTY_VALUE}</span>;
  return (
    <span className="flex flex-wrap justify-end gap-1.5">
      {items.map((item, index) => (
        <Badge key={`${item}-${index}`} variant="outline" className="border-[#062B1A]/12 bg-white/70 font-normal">
          {numbered ? `${index + 1}. ${item}` : item}
        </Badge>
      ))}
    </span>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          <Icon className="h-4 w-4 text-[#D4AF37]" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export default function AgencyProfile() {
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getMyAgencyProfile();
        if (!cancelled) setAgency(res.data || null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setAgency(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        {[1, 2, 3].map((item) => (
          <div key={item} className="h-32 animate-pulse rounded-lg bg-white" />
        ))}
      </div>
    );
  }

  const mainContact = agency?.mainContact || {};
  const secondaryContact = agency?.secondaryContact || {};
  const documents = safeList(agency?.documents);
  const companyRegistrationDocument = documents.find((document) => document.doc_type === 'company_registration');
  const governmentIdDocument = documents.find((document) => document.doc_type === 'government_id');
  const manualDocuments = safeList(agency?.manualDocuments);

  const downloadDocument = async (document) => {
    try {
      const res = await getMyAgencyDocumentDownload(document.id);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <ProfileHero
        avatar={(
          <Avatar className="h-24 w-24 border-4 border-white shadow-md">
            <AvatarFallback className="bg-[#D4AF37] text-2xl font-bold text-[#062B1A]">
              {initials(displayAgencyName(agency))}
            </AvatarFallback>
          </Avatar>
        )}
      >
        <h2 className="break-words text-2xl font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>
          {displayAgencyName(agency)}
        </h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90">
          <span className="inline-flex min-w-0 items-center gap-2 break-all">
            <Mail className="h-4 w-4 shrink-0" /> {agency?.email || EMPTY_VALUE}
          </span>
          {agency?.phone && (
            <span className="inline-flex items-center gap-2">
              <Phone className="h-4 w-4" /> {agency.phone}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <StatusBadge status={agency?.status} />
          {agency?.tier !== undefined && (
            <Badge variant="outline" className="border-white/40 bg-white font-semibold text-[#062B1A]">
              Tier {agency.tier}
            </Badge>
          )}
        </div>
      </ProfileHero>

      <AccountSettingsCard />

      <AgencyAgreementCard mode="self" />

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          1. Agency Contact Information
        </h3>
        <div className="grid grid-cols-1 gap-6">
          <SectionCard title="Company Identity" icon={Building2}>
            <InfoRow label="Company legal name" value={agency?.company_name} />
            <InfoRow label="Company preferred/brand name" value={agency?.preferredName} />
            <InfoRow label="Company phone number" value={agency?.phone} />
            <InfoRow label="Company email address" value={agency?.email} />
            <InfoRow label="Company website" value={agency?.website} />
          </SectionCard>

          <SectionCard title="Company Address" icon={MapPin}>
            <InfoRow label="Apt/Unit/Suite #" value={agency?.unitNumber} />
            <InfoRow label="Street Address" value={agency?.address} />
            <InfoRow label="City" value={agency?.city} />
            <InfoRow label="Country" value={agency?.country} />
            <InfoRow label="Province/State" value={fullRegionName(agency?.country, agency?.province)} />
            <InfoRow label="Postal Code" value={agency?.postalCode} />
          </SectionCard>

          <SectionCard title="Main Contact Person" icon={UserRound}>
            <InfoRow label="Full name" value={contactName(mainContact) || agency?.agent_name} />
            <InfoRow label="Preferred name" value={mainContact.preferredName} />
            <InfoRow label="Job title" value={mainContact.title} />
            <InfoRow label="Phone number" value={mainContact.phone || agency?.phone} />
            <InfoRow label="Email address" value={mainContact.email || agency?.email} />
            <InfoRow label="WeChat ID" value={mainContact.wechatId} />
          </SectionCard>

          <SectionCard title="Secondary Contact Person" icon={Users}>
            <InfoRow label="Full name" value={contactName(secondaryContact)} />
            <InfoRow label="Preferred name" value={secondaryContact.preferredName} />
            <InfoRow label="Job title" value={secondaryContact.title} />
            <InfoRow label="Phone number" value={secondaryContact.phone} />
            <InfoRow label="Email address" value={secondaryContact.email} />
          </SectionCard>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          2. Agency Business Information
        </h3>
        <div className="grid grid-cols-1 gap-6">
          <SectionCard title="Business Profile" icon={Target}>
            <InfoRow label="Nature of business" value={agency?.natureOfBusiness} />
            <InfoRow label="Number of Employees" value={agency?.numberOfEmployees} />
            <InfoRow label="GST Number" value={agency?.gstNumber} />
            <InfoRow label="Company Registration Number" value={agency?.companyRegistrationNumber} />
            <InfoRow label="Agency type" value={agency?.agencyType} />
          </SectionCard>

          <SectionCard title="Student Market" icon={Globe2}>
            <InfoRow label="Students sent to Canada" value={agency?.studentsSentToCanadaPast12Months} />
            <InfoRow label="Students anticipated for MCC" value={agency?.studentsAnticipatedForMccNext12Months} />
            <InfoRow label="Top student countries" value={<BadgeList values={agency?.studentSourceCountries} />} />
            <InfoRow label="Target education levels" value={<BadgeList values={agency?.targetEducationLevels} />} />
          </SectionCard>

          <SectionCard title="Program and Promotion" icon={Target}>
            <InfoRow label="Top programs of interest" value={<BadgeList values={agency?.programsOfInterest} numbered />} />
            <InfoRow label="Promotional channels" value={<BadgeList values={agency?.promotionalChannels} />} />
            <InfoRow label="How did you hear about MCC?" value={agency?.howDidYouHearAboutMcc} />
            <InfoRow label="Communication channels" value={<BadgeList values={agency?.communicationChannels} />} />
          </SectionCard>

          <SectionCard title="Documents" icon={FileCheck2}>
            <InfoRow label="Company registration document" value={companyRegistrationDocument?.filename || EMPTY_VALUE} />
            <InfoRow label="Government ID document" value={governmentIdDocument?.filename || EMPTY_VALUE} />
            <InfoRow label="Required document" value={agency?.requiredDocumentLabel} />
            <InfoRow label="Last updated" value={formatDate(agency?.updated_at)} />
          </SectionCard>

          <SectionCard title="Contracts & additional documents" icon={FileCheck2}>
            {manualDocuments.length ? (
              <div className="divide-y divide-[#062B1A]/8">
                {manualDocuments.map((document) => (
                  <div key={document.id} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#062B1A]">{document.title || document.filename}</p>
                      <p className="mt-1 text-xs text-[#062B1A]/50">
                        {[document.filename, document.uploaded_at ? `Uploaded ${formatDate(document.uploaded_at)}` : ''].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => downloadDocument(document)}>
                      <Download className="mr-2 h-4 w-4" /> Download
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#062B1A]/45">No contracts or additional documents are on file.</p>
            )}
          </SectionCard>
        </div>
      </section>
    </div>
  );
}
